import { checkUserStatus } from './contractUtils';

export const BNB_CHAIN_ID = '0x38'; 
export const BNB_CHAIN_CONFIG = {
  chainId: BNB_CHAIN_ID,
  chainName: 'BNB Chain',
  nativeCurrency: {
    name: 'BNB',
    symbol: 'BNB',
    decimals: 18
  },
  rpcUrls: ['https://bsc-dataseed.binance.org/'],
  blockExplorerUrls: ['https://bscscan.com']
};

export const checkIfWalletIsInstalled = () => {
  const { ethereum } = window;
  return Boolean(ethereum && ethereum.isMetaMask);
};

export const switchToBNBChain = async () => {
  try {
    await window.ethereum.request({
      method: 'wallet_switchEthereumChain',
      params: [{ chainId: BNB_CHAIN_ID }],
    });
    return true;
  } catch (switchError) {
    if (switchError.code === 4902) {
      try {
        await window.ethereum.request({
          method: 'wallet_addEthereumChain',
          params: [BNB_CHAIN_CONFIG],
        });
        return true;
      } catch (addError) {
        return false;
      }
    }
    return false;
  }
};

export const getSignature = async (address) => {
  try {
    const message = `Welcome to Burn The Blocks!\n\nWallet: ${address}\nTime: ${new Date().toISOString()}`;
    const signature = await window.ethereum.request({
      method: 'personal_sign',
      params: [message, address],
    });
    return signature;
  } catch (error) {
    return null;
  }
};

export const formatAddress = (address) => {
  if (!address) return '';
  return `${address.slice(0, 4)}...${address.slice(-4)}`;
};

const WALLET_CACHE_KEY = 'wallet_connection_cache';

const saveWalletToCache = (address, signature) => {
    const cacheData = {
        address,
        signature,
        timestamp: new Date().getTime()
    };
    localStorage.setItem(WALLET_CACHE_KEY, JSON.stringify(cacheData));
};

const getWalletFromCache = () => {
    const cacheData = localStorage.getItem(WALLET_CACHE_KEY);
    if (!cacheData) return null;

    const { address, signature, timestamp } = JSON.parse(cacheData);
    if (new Date().getTime() - timestamp > 24 * 60 * 60 * 1000) {
        localStorage.removeItem(WALLET_CACHE_KEY);
        return null;
    }

    return { address, signature };
};

export const clearWalletCache = () => {
    localStorage.removeItem(WALLET_CACHE_KEY);
};

export const autoConnectWallet = async (onNFTDetected) => {
    try {
        const cachedWallet = getWalletFromCache();
        if (!cachedWallet) return null;

        if (!checkIfWalletIsInstalled()) {
            clearWalletCache();
            return null;
        }

        const accounts = await window.ethereum.request({
            method: 'eth_accounts' 
        });

        if (accounts[0]?.toLowerCase() === cachedWallet.address.toLowerCase()) {
            const address = accounts[0];
            
            try {
                const userStatus = await checkUserStatus(address);
                if (userStatus && userStatus.is_checked_in) {
                    if (onNFTDetected) {
                        onNFTDetected();
                    }
                }
            } catch (error) {
                throw error;
            }

            return address;
        } else {
            clearWalletCache();
            return null;
        }
    } catch (error) {
        clearWalletCache();
        return null;
    }
};

export const connectAndSignWallet = async (onNFTDetected) => {
    try {
        if (!checkIfWalletIsInstalled()) {
            throw new Error('Please install MetaMask first');
        }

        const accounts = await window.ethereum.request({
            method: 'eth_requestAccounts'
        });
        const address = accounts[0];

        const signature = await getSignature(address);
        if (!signature) {
            throw new Error('Signature required to proceed');
        }

        saveWalletToCache(address, signature);

        try {
            const userStatus = await checkUserStatus(address);
            if (userStatus && userStatus.is_checked_in) {
                if (onNFTDetected) {
                    onNFTDetected();
                }
            }
        } catch (error) {
            throw error;
        }

        return address;
    } catch (error) {
        throw error;
    }
};

export const setupWalletListeners = (onAccountsChanged, onDisconnect) => {
    if (!window.ethereum) return;

    window.ethereum.on('accountsChanged', (accounts) => {
        if (accounts.length === 0) {
            clearWalletCache();
            onDisconnect?.();
        } else {
            onAccountsChanged?.(accounts[0]);
        }
    });

    window.ethereum.on('disconnect', () => {
        clearWalletCache();
        onDisconnect?.();
    });
};